export const GTMEventsNames = [
  "login",
  "sign_up",
  "session_start",
  "page_view",
  "add_to_cart",
  "remove_from_cart",
  "COUPON_ADDED",
  "COUPON_REMOVED",
  "view_cart",
  "DIET_ADD",
  "ADDRESS_ADDED",
  "begin_checkout",
  "PURCHASE_START",
  "add_payment_info",
  "PURCHASE_WAIT",
  "PURCHASE_FAIL",
  "purchase",
  "select_item",
  "view_item",
  "view_item_list",
  "diet_type_add",
  "diet_type_remove",
  "DIET_EXTEND_START",
  "DIET_RENEWAL_START",
  "SHIPPING_CHANGED",
  "CUSTOMIZE_DIET_END",
  "CUSTOMIZE_DIET",
  "CUSTOMIZE_DIET_EDIT",
  "user_data",
  "LOGOUT",
  "SHOW_MENU",
  "MENU_RESPONSE_TIME",
  "SWAP_DIET",
  "SWAP_SELECTED",
  "generate_lead",
  "COPY_CODE",
  "NO_DELIVERY",
  "select_content",
  "view_promotion",
] as const;
export type GTMEventsNamesType = (typeof GTMEventsNames)[number];

export type GTMCartItemType = {
  item_id: string;
  item_name: string;
  coupon?: string;
  item_brand?: string; // catering company
  item_category?: string; // main diet category
  item_category2?: string; // calories
  item_category3?: number; // timestamp first day
  item_category4?: number; // timestamp last day
  item_variant?: string; // diet witch choice
  price?: number;
  quantity?: number; // number of items
  duration?: number; // number of days
};

export type GTMEvents =
  | EventLoginType
  | EventLogoutType
  | EventSignUpType
  | EventAddToCartType
  | EventRemoveFromCartType
  | EventCouponAddedType
  | EventCouponRemovedType
  | EventViewCartType
  | EventDietAddType
  | EventAddressAddedType
  | EventBeginCheckoutType
  | EventAddPaymentInfoType
  | EventPurchaseStartType
  | EventPurchaseWaitType
  | EventPurchaseFailType
  | EventPurchaseType
  | EventSelectItemType
  | EventViewItemType
  | EventViewItemListType
  | EventDietTypeAddType
  | EventDietTypeRemoveType
  | EventDietExtensionStartType
  | EventShippingChangedType
  | EventCustomizeDietEndType
  | EventCustomizeDietType
  | EventCustomizeDietEditType
  | EventUserDataType
  | EventShowMenuType
  | EventMenuResponseTimeType
  | EventSwapDietType
  | EventSwapSelectedDietType
  | EventDietRenewalStartType
  | EventGenerateLeadType
  | EventCopyCodeType
  | EventNoDeliveryType
  | EventSelectContentType
  | EventViewPromotionType;

export type EventViewPromotionType = {
  event: "view_promotion";
  ecommerce: {
    creative_name: "lista_kuponow";
    creative_slot: "panel_uzytkownika" | "koszyk";
    coupons_count: number;
  };
};

export type EventNoDeliveryType = {
  event: "NO_DELIVERY";
  ecommerce: {
    link_text: string;
  };
};

export type CopyCodeSourceType = "panel_uzytkownika" | "koszyk";

export type EventCopyCodeType = {
  event: "COPY_CODE";
  coupon: string;
  item_brand: string;
  coupon_value: string | null;
  source: string;
};

export type EventGenerateLeadType = {
  event: "generate_lead";
  coupon: string;
};

export type EventLoginType = {
  event: "login";
  value: {
    method: "email" | "google" | "facebook" | "apple";
    user_id: string;
  };
};

export type EventLogoutType = {
  event: "LOGOUT";
  value: {
    user_id: null;
  };
};

export type EventSignUpType = {
  event: "sign_up";
  value: EventLoginType["value"];
};

// Cart type, prefixed on the extend and reorder flows so their purchases can
// be told apart from new orders (MPL-2581).
export type CheckoutAffiliation =
  | "classic"
  | "multidiet"
  | "extend-classic"
  | "extend-multidiet"
  | "renewal-classic"
  | "renewal-multidiet";

export type GtmPaymentType = "szybki_blik" | "payu";

export type EventAddToCartType = {
  event: "add_to_cart";
  ecommerce: {
    cartId?: string;
    currency: string;
    value: number;
    items: GTMCartItemType[];
    affiliation?: CheckoutAffiliation;
  };
};

export type EventRemoveFromCartType = {
  event: "remove_from_cart";
  ecommerce: EventAddToCartType["ecommerce"];
};

export type CouponSourceType =
  | "panel_przycisk"
  | "koszyk_lista_przycisk"
  | "koszyk_recznie";

export type EventCouponAddedType = {
  event: "COUPON_ADDED";
  ecommerce: {
    cartId?: string;
    coupon: string;
    status: "success" | "fail";
    link_text?: string;
    item_brand: string;
    coupon_value: string;
    source: CouponSourceType;
  };
};

export type EventCouponRemovedType = {
  event: "COUPON_REMOVED";
  ecommerce: {
    cartId?: string;
    coupon: string;
    item_brand: string; // catering company name, or "MULTIBRAND" for >1 brand
    coupon_value: string; // e.g. "55%" or "150PLN"
  };
};

export type EventViewCartType = {
  event: "view_cart";
  ecommerce: EventAddToCartType["ecommerce"] & {
    coupon?: string;
  };
};

export type EventDietAddType = {
  event: "DIET_ADD";
};

export type EventAddressAddedType = {
  event: "ADDRESS_ADDED";
  user_data: {
    geo: string; // postal code
    city: string; // no polish letters, no special characters
  };
};

export type EventBeginCheckoutType = {
  event: "begin_checkout";
  ecommerce: EventViewCartType["ecommerce"];
};

export type EventPurchaseStartType = {
  event: "PURCHASE_START";
  ecommerce: EventBeginCheckoutType["ecommerce"] & {
    order_no: number;
    transaction_id: string;
    shipping: number; // shipping cost value
    discount: number; // discount value
    value: number; // value gross
    payment_type: GtmPaymentType;
  };
};

export type EventAddPaymentInfoType = {
  event: "add_payment_info";
  data: EventViewCartType["ecommerce"] & {
    payment_type: GtmPaymentType;
  };
};

export type EventPurchaseWaitType = {
  event: "PURCHASE_WAIT";
  ecommerce: EventPurchaseStartType["ecommerce"];
};

export type EventPurchaseFailType = {
  event: "PURCHASE_FAIL";
  ecommerce: EventPurchaseStartType["ecommerce"];
};

export type EventPurchaseType = {
  event: "purchase";
  ecommerce: EventPurchaseStartType["ecommerce"];
};

export type EventSelectItemType = {
  event: "select_item";
  data: {
    item_list_name: string; // page path
    items: {
      item_id: string;
      item_name: string;
      discount?: number;
      index?: number;
      item_list_name?: string; // page path
      item_brand?: string;
      item_variant?: string;
      price?: number;
      quantity?: number;
    }[];
  };
};
export type EventShowMenuType = {
  event: "SHOW_MENU";
  ecommerce: {
    items: {
      item_id: string;
      item_name: string;
      item_brand?: string;
      price?: number;
      discount?: number;
      index?: number;
      item_list_name?: string; // page path
    }[];
  };
};

export type EventMenuResponseTimeType = {
  event: "MENU_RESPONSE_TIME";
  loading_time_sec: number;
  loading_time_unit: "seconds";
};

export type EventSelectContentType = {
  event: "select_content";
  content_type: string;
  link_text: "catering" | "diety";
};

export type EventViewItemType = {
  event: "view_item";
  ecommerce: {
    currency: string;
    value: number;
    items: GTMCartItemType[];
  };
};

export type EventViewItemListType = {
  event: "view_item_list";
  diet_type?: string;
  meals_count?: string | number;
  kcal?: string | number;
  search_term?: string;
  price_from?: string | number;
  price_to?: string | number;
  sort?: string;
  ecommerce: {
    item_list_name: string;
    items: {
      item_id: string;
      item_name: string;
      item_brand?: string;
      index: number;
      price?: number;
    }[];
  };
};

export type EventDietTypeAddType = {
  event: "diet_type_add";
  data: {
    type: string;
  };
};

export type EventDietTypeRemoveType = {
  event: "diet_type_remove";
  data: {
    type: string;
  };
};

export type EventDietExtensionStartType = {
  event: "DIET_EXTEND_START";

  ecommerce: {
    currency: string;
    value: number;
    items: GTMCartItemType[];
    cartId?: string;
    affiliation?: "extend-multidiet" | "extend-classic";
    coupon?: string;
    diet_id: string;
  };
};

export type EventDietRenewalStartType = Omit<
  EventDietExtensionStartType,
  "event" | "ecommerce"
> & {
  event: "DIET_RENEWAL_START";

  ecommerce: Omit<EventDietExtensionStartType["ecommerce"], "affiliation"> & {
    affiliation?: "renewal-multidiet" | "renewal-classic";
  };
};

export type EventShippingChangedType = {
  event: "SHIPPING_CHANGED";
  data: {
    items: GTMCartItemType[];
  };
};

export type EventCustomizeDietEndType = {
  event: "CUSTOMIZE_DIET_END";
  ecommerce: EventViewItemType["ecommerce"];
};

export type EventCustomizeDietType = {
  event: "CUSTOMIZE_DIET";
  ecommerce: {
    items: {
      item_id: string;
      item_name: string;
      item_brand?: string;
      item_category?: string;
    }[];
  };
};

export type EventCustomizeDietEditType = {
  event: "CUSTOMIZE_DIET_EDIT";
  ecommerce: EventCustomizeDietType["ecommerce"];
};

export type EventUserDataType = {
  event: "user_data";
  value: EventUserDataValue;
};

export type EventUserDataValue = {
  method?: "email" | "google" | "facebook" | "apple" | null;
  user_id?: string | null;
  page: string;
  stream: UserPlatformType;
};

export type UserPlatformType = "web" | "ios app" | "android app" | null;

export type EventSwapDietType = {
  event: "SWAP_DIET";
  ecommerce: {
    items: {
      item_id: string;
      item_name: string;
      discount?: number;
      index?: number;
      item_brand?: string;
      item_list_name?: string;
      price?: number;
    }[];
  };
};

export type EventSwapSelectedDietType = {
  event: "SWAP_SELECTED";
  ecommerce: {
    items: {
      item_id: string;
      item_name: string;
      discount?: number;
      index?: number;
      item_brand?: string;
      item_list_name?: string;
      price?: number;
    }[];
  };
};
